/**
 * Story layer kit (docs/IDEAS-BACKLOG.md §E): flags, cast figures / faces, character voices, "met" state.
 * Data + selection logic live in core `story/` (registry of every world's story.ts).
 */
import { WORLD_PLANS } from '../../lib/content';
import { memo, useMemo, type ButtonHTMLAttributes, type ReactNode } from 'react';
import {
  DEFAULT_STORY_OFFER,
  castKey,
  localize,
  meetCharacters,
  setProfilePref,
  storyForWorld,
  type L,
  type Lang,
  type PathId,
  type StoryCastMember,
  type StoryGuide,
  type StoryOfferMode,
} from '@sketchling/core';
import { play } from '../../lib/sfx';
import { Icon, type IconName } from '../icons';
import { useApp, useCollection } from '../../state/store';
import { drawingById } from '../../lib/content';
import { speak } from '../../lib/tts';
import { DrawingView } from '../DrawingView';
import { WORLD_ART } from '../../art/worlds';
import { StoryGuideArt } from '../StoryGuide';
import { Scribble, TeacherJuno } from '../characters';
import './story.css';

/** Stable empty list (zustand rule: never return a fresh array from a selector). */
const EMPTY_MET: string[] = [];

export interface StoryFlags {
  layer: boolean;
  narration: boolean;
  comics: boolean;
  book: boolean;
}

/** Story flags (each a primitive selector, so no re-render loops). Sub-features need the master switch. */
export function useStoryFlags(): StoryFlags {
  const layer = useApp((s) => s.config.storyLayer !== false);
  const narration = useApp((s) => s.config.storyNarration !== false);
  const comics = useApp((s) => s.config.storyComics !== false);
  const book = useApp((s) => s.config.storybookExport !== false);
  return useMemo(() => ({ layer, narration: layer && narration, comics: layer && comics, book: layer && book }), [layer, narration, comics, book]);
}

/** Cast members the active profile has met (`world:castId`). */
export function useMet(): string[] {
  const c = useCollection();
  return c.met ?? EMPTY_MET;
}

export function rememberMet(profileId: string, world: string, castId: string): void {
  useApp.getState().updateCollect(profileId, (c) => meetCharacters(c, [castKey(world, castId)]));
}

/* ------------------------------------------ per-profile story preferences ------------------------------------------ */

export interface StoryPrefs {
  /** "Story before lessons": always / first time only (default) / off. */
  mode: StoryOfferMode;
  /** Characters narrate the drawing steps. */
  narration: boolean;
  /** "Draw now" picks in a row (≥ STORY_SKIP_LIMIT → no automatic offers). */
  skips: number;
}

/** Story preferences of the active profile (synced in progress.collect.prefs; primitives only → no re-render loops). */
export function useStoryPrefs(): StoryPrefs {
  const c = useCollection();
  const mode = c.prefs?.storyMode?.v ?? DEFAULT_STORY_OFFER;
  const narration = c.prefs?.storyNarration?.v ?? true;
  const skips = c.prefs?.storySkips?.v ?? 0;
  return useMemo(() => ({ mode, narration, skips }), [mode, narration, skips]);
}

/** Write one story preference for a profile (defaults to the active one). */
export function setStoryPref<K extends 'storyMode' | 'storyNarration' | 'storySkips'>(key: K, v: K extends 'storyMode' ? StoryOfferMode : K extends 'storyNarration' ? boolean : number, profileId?: string): void {
  const st = useApp.getState();
  const id = profileId ?? st.activeProfileId;
  if (!id) return;
  st.updateCollect(id, (c) => setProfilePref(c, key, v as never, new Date().toISOString()));
}

/** The child picked "Draw now" (+1 in a row) or "Hear the story" (reset). */
export function noteStoryChoice(choice: 'draw' | 'story', profileId?: string): void {
  const st = useApp.getState();
  const id = profileId ?? st.activeProfileId;
  if (!id) return;
  const cur = st.progress[id]?.collect.prefs?.storySkips?.v ?? 0;
  setStoryPref('storySkips', choice === 'draw' ? Math.min(99, cur + 1) : 0, id);
}

/* ------------------------------------------ Storybook buttons ------------------------------------------ */

type SbVariant = 'go' | 'paper' | 'ink';
/**
 * Storybook button (story layer): big pill (≥ 52 px), warm-ink outline, WCAG AA text (go = white on #A93F17 6.2:1,
 * paper = ink on cream 11.5:1), a clear icon and an arrow when it moves the story on, pressed = pushed into the page.
 */
export function StoryButton({
  variant = 'go',
  icon,
  arrow,
  big,
  children,
  onClick,
  className = '',
  ...rest
}: { variant?: SbVariant; icon?: IconName; arrow?: boolean; big?: boolean; children: ReactNode } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`sb-btn sb-btn--${variant}${big ? ' sb-btn--big' : ''} ${className}`}
      onClick={(e) => {
        play('click');
        onClick?.(e);
      }}
      {...rest}
    >
      {icon && <Icon name={icon} size={big ? 26 : 22} />}
      <span className="sb-btn-label">{children}</span>
      {arrow && (
        <span className="sb-btn-arrow" aria-hidden>
          <Icon name="forward" size={20} />
        </span>
      )}
    </button>
  );
}

/** Round Storybook icon button (≥ 52 px): page arrows, play / pause, close. */
export function StoryRound({ icon, label, className = '', ...rest }: { icon: IconName; label: string } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className={`sb-round ${className}`} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={26} />
    </button>
  );
}

/** The world's story guide id (StoryGuideArt). */
export const guideOf = (world: string): StoryGuide => (WORLD_PLANS.find((p) => p.id === world)?.planV2?.story.guide as StoryGuide | undefined) ?? 'scribble';

/** Speak a line in a character's voice (deterministic in `?mockTts=1`). */
export function speakAs(cast: Pick<StoryCastMember, 'voice'> | null | undefined, text: string, lang?: Lang): Promise<void> {
  return speak(text, lang, cast ? { pitch: cast.voice.pitch, rate: cast.voice.rate } : undefined);
}

/** True in the deterministic test mode unless the story e2e asked to drive the story screens by hand. */
try {
  if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('storyE2E')) sessionStorage.setItem('sk-story-e2e', '1');
} catch {
  /* ignore */
}
export function storyAutoClose(): boolean {
  try {
    return !!sessionStorage.getItem('sk-mock-tts') && !sessionStorage.getItem('sk-story-e2e');
  } catch {
    return false;
  }
}

/** A cast member as a figure: Juno / Scribble / the world guide / the lesson drawing that IS the character. */
export const CastFigure = memo(function CastFigure({ world, cast, size = 120, className = '', mood }: { world: string; cast: StoryCastMember; size?: number; className?: string; mood?: 'happy' | 'wow' }) {
  const host = storyForWorld(world)?.host;
  if (cast.id === 'juno') return <TeacherJuno face={mood === 'wow' ? 'wow' : 'smile'} size={size} className={className} />;
  if (cast.id === 'scribble') return <Scribble mood={mood === 'wow' ? 'surprised' : 'happy'} size={size} className={className} />;
  if (cast.id === host) return <StoryGuideArt who={guideOf(world)} size={size} className={className} />;
  const d = cast.drawingId ? drawingById(cast.drawingId) : undefined;
  if (d)
    return (
      <span className={`sty-fig ${className}`} style={{ width: size, height: size }} data-cast={cast.id}>
        <DrawingView drawing={d} paper={false} />
      </span>
    );
  return (
    <span className={`sty-fig sty-mono ${className}`} style={{ width: size, height: size, fontSize: size * 0.42 }} data-cast={cast.id} aria-hidden>
      {localize(cast.name, 'en').slice(0, 1)}
    </span>
  );
});

/** Round face badge of a cast member (narration / reaction bubbles). */
export function CastFace({ world, cast, size = 44 }: { world: string; cast: StoryCastMember; size?: number }) {
  return (
    <span className="sty-face" style={{ width: size, height: size }} aria-hidden>
      <CastFigure world={world} cast={cast} size={Math.round(size * 1.25)} />
    </span>
  );
}

/** A drawing used as a comic prop / figure. */
export function DrawingFigure({ id, size = 110 }: { id: string; size?: number }) {
  const d = drawingById(id);
  if (!d) return null;
  return (
    <span className="sty-fig" style={{ width: size, height: size }} data-drawing={id}>
      <DrawingView drawing={d} paper={false} />
    </span>
  );
}

/** Sky gradient of a world (comic panels, meet sheet). */
export function worldSkyStyle(world: string): { background: string } {
  const th = WORLD_ART[world as PathId]?.theme;
  if (!th) return { background: 'linear-gradient(180deg, #CFE8F2, #FFF4E2)' };
  return { background: `linear-gradient(180deg, ${th.sky} 0%, ${th.skyLow} 72%, ${th.card.border} 100%)` };
}

/** Accent colours of a world (comic captions, card trims). */
export const worldAccents = (world: string): [string, string, string] => WORLD_ART[world as PathId]?.theme.accents ?? ['#E9783A', '#F2C14E', '#3F9C9A'];

export const loc2 = (l: L, lang: Lang) => l[lang] || l.en;
export const asPath = (w: string) => w as PathId;
